import { useEffect } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronRight } from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'
import { Footer, HeaderMiddle, Navbar, TopBar, WA_NUMBER } from '../components/SiteChrome'
import { WhatsAppChannelSection } from '../components/WhatsAppChannelSection'
import { whatsAppCtaProps, track, trackSearch, trackSelectItem } from '../lib/analytics'
import {
  getProductCategoryName,
  getProductPrice,
  getProductPrimaryImage,
  listStorefrontCategories,
  listStorefrontProducts,
  placeholderImage,
  toExternalImage,
} from '../lib/dropify'
import type { StorefrontCategory, StorefrontProduct } from '../lib/dropify'
import { BRANDS, getBrandLandingHref } from '../data/brands'
import { DEFAULT_OG_IMAGE, absoluteUrl } from '../lib/seo'

type SearchParams = {
  q?: string
}

const PRODUCT_CARD_IMAGE_TRANSFORM = { width: 320, height: 320, smart: true, format: 'webp', quality: 58 } as const

export const Route = createFileRoute('/search')({
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    const q = typeof search.q === 'string' && search.q !== '' ? search.q : undefined
    return q === undefined ? {} : { q }
  },
  loaderDeps: ({ search }) => ({ q: search.q ?? '' }),
  loader: async ({ deps }) => {
    const q = deps.q.trim()
    const [categories, response] = await Promise.all([
      listStorefrontCategories().catch(() => []),
      q ? listStorefrontProducts({ search: q, per_page: 100 }) : Promise.resolve({
        items: [],
        total: 0,
        page: 1,
        per_page: 100,
        total_pages: 0,
      }),
    ])

    return {
      categories,
      q,
      products: response.items,
      total: response.total,
    }
  },
  head: ({ loaderData }) => {
    const q = loaderData?.q ?? ''
    const title = q ? `Hasil Pencarian "${q}" | Dropify` : 'Cari Produk | Dropify'
    const description = q
      ? `Hasil pencarian produk untuk kata kunci ${q}.`
      : 'Cari produk Dropify berdasarkan nama atau SKU.'

    return {
      meta: [
        { title },
        { name: 'description', content: description },
        { name: 'robots', content: 'noindex,follow' },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:url', content: q ? absoluteUrl(`/search?q=${encodeURIComponent(q)}`) : absoluteUrl('/search') },
        { property: 'og:image', content: DEFAULT_OG_IMAGE },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: DEFAULT_OG_IMAGE },
      ],
      links: [
        { rel: 'canonical', href: absoluteUrl('/search') },
      ],
    }
  },
  component: SearchPage,
})

function SearchResultCard({ product }: { product: StorefrontProduct }) {
  const href = `/produk/${product.slug || product.id}`

  const selectItem = () =>
    trackSelectItem(
      {
        item_id: product.id,
        item_name: product.name,
        item_category: getProductCategoryName(product),
        price: getProductPrice(product),
      },
      'search_results',
    )

  return (
    <div className="group bg-white rounded-xl overflow-hidden border border-slate-100 hover:border-blue-200 hover:shadow-lg transition-all duration-200">
      <a href={href} onClick={selectItem} className="block relative overflow-hidden bg-slate-100" style={{ height: 260 }}>
        <img
          src={getProductPrimaryImage(product, PRODUCT_CARD_IMAGE_TRANSFORM)}
          alt={product.name}
          width="320"
          height="320"
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </a>
      <div className="p-4">
        <p className="text-xs text-blue-600 font-semibold uppercase tracking-wider mb-1">{getProductCategoryName(product)}</p>
        <a href={href}>
          <h3 className="text-sm font-semibold text-slate-800 mb-2 leading-snug hover:text-blue-600 transition-colors line-clamp-2">
            {product.name}
          </h3>
        </a>
        <a
          href={href}
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg transition-colors w-full justify-center"
        >
          Lihat detail
        </a>
      </div>
    </div>
  )
}

const EMPTY_STATE_IMAGE_TRANSFORM = { width: 240, height: 240, smart: true, format: 'webp', quality: 55 } as const

// Synonyms and near-miss terms seen in GA4 site search that the product index
// doesn't match, mapped to the closest stocked category. Only categories that
// exist in the live `categories` response are ever suggested, so a stale slug
// here degrades to "no suggestion" instead of a broken link.
const CATEGORY_SUGGESTIONS: Array<{ terms: string[]; slug: string }> = [
  { terms: ['korset', 'kamisol', 'camisole', 'singlet', 'tank top', 'longtorso'], slug: 'camisol-longtorso' },
  { terms: ['celana dalam', 'cd', 'boxer', 'sempak', 'dalaman', 'underwear'], slug: 'cd-underwear' },
  { terms: ['piyama', 'pajama', 'baju tidur', 'daster', 'oneset', 'homedress'], slug: 'piyama-oneset' },
  { terms: ['bh', 'bra', 'miniset', 'lingerie'], slug: 'bra-lingerie' },
  { terms: ['handuk', 'towel'], slug: 'handuk' },
  { terms: ['mukena'], slug: 'mukena-premium' },
  { terms: ['kaos kaki', 'kaus kaki', 'socks'], slug: 'kaos-kaki' },
  { terms: ['masker'], slug: 'masker' },
  { terms: ['manset', 'manshet', 'handsock', 'sarung tangan'], slug: 'manshet-sarung-tangan' },
  { terms: ['baju anak', 'kaos anak', 'pakaian anak'], slug: 'baju-anak' },
  { terms: ['bayi', 'baby', 'newborn'], slug: 'perlengkapan-bayi' },
]

function findCategorySuggestion(q: string, categories: StorefrontCategory[]): StorefrontCategory | null {
  const query = q.toLowerCase()
  for (const { terms, slug } of CATEGORY_SUGGESTIONS) {
    if (!terms.some((term) => new RegExp(`\\b${term}\\b`).test(query))) continue
    const category = categories.find((c) => c.slug === slug)
    if (category) return category
  }
  return null
}

// Zero-result state. GA4 showed dead-end searches for unstocked items (minyak
// goreng, sendal, pancing) AND for stocked brands that just miss the index —
// both got a bare "no results" wall before. Offer the closest self-serve paths
// plus a WhatsApp fallback so the high-intent searcher isn't lost.
function EmptySearchState({ q, categories }: { q: string; categories: StorefrontCategory[] }) {
  const waMessage = `Halo Dropify, saya mencari "${q}" dan belum menemukannya di website. Mohon periksa apakah produk tersebut tersedia.`
  const brandMatch = BRANDS.find((brand) => q.toLowerCase().includes(brand.searchTerm))
  const categorySuggestion = findCategorySuggestion(q, categories)

  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-6 py-12">
      <div className="mx-auto max-w-2xl text-center">
        <h2 className="text-xl font-black tracking-tight text-slate-900">
          Produk "{q}" belum tersedia di katalog
        </h2>
        <p className="mt-2 text-base leading-relaxed text-slate-500">
          Tidak semua produk ditampilkan pada hasil pencarian. Kirim nama atau foto barang kepada admin
          agar ketersediaannya dapat diperiksa.
        </p>
        <a
          href={`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(waMessage)}`}
          {...whatsAppCtaProps('search', { cta_placement: 'search_empty', search_term: q })}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-5 inline-flex items-center justify-center gap-2.5 rounded-lg bg-green-700 px-6 py-3.5 text-base font-bold text-white shadow-sm transition-colors hover:bg-green-800"
        >
          <FontAwesomeIcon icon={faWhatsapp} className="h-5 w-5" />
          Periksa ketersediaan via WhatsApp
        </a>
        {brandMatch && (
          <p className="mt-4 text-sm text-slate-500">
            Mencari merek {brandMatch.name}?{' '}
            <a href={getBrandLandingHref(brandMatch)} className="font-semibold text-blue-600 hover:text-blue-700">
              Lihat semua produk {brandMatch.name} di sini
            </a>
          </p>
        )}
        {!brandMatch && categorySuggestion && (
          <p className="mt-4 text-sm text-slate-500">
            Produk seperti ini kemungkinan ada di kategori {categorySuggestion.name}.{' '}
            <a
              href={`/kategori/${categorySuggestion.slug}`}
              onClick={() =>
                track('click_internal_link', {
                  link_url: `/kategori/${categorySuggestion.slug}`,
                  link_text: categorySuggestion.name,
                  link_location: 'search_empty_category_suggestion',
                })
              }
              className="font-semibold text-blue-600 hover:text-blue-700"
            >
              Lihat kategori {categorySuggestion.name}
            </a>
          </p>
        )}
      </div>

      {categories.length > 0 && (
        <div className="mx-auto mt-10 max-w-4xl">
          <p className="mb-4 text-center text-xs font-bold uppercase tracking-wider text-slate-500">
            Atau jelajahi kategori kami
          </p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            {categories.slice(0, 8).map((category) => (
              <a
                key={category.slug}
                href={`/kategori/${category.slug}`}
                className="group overflow-hidden rounded-xl border border-slate-100 bg-slate-50 transition-all hover:border-blue-200 hover:shadow-md"
              >
                <img
                  src={toExternalImage(category.image_url, EMPTY_STATE_IMAGE_TRANSFORM) || placeholderImage(category.name, 240, 240)}
                  alt={`Grosir ${category.name}`}
                  width="240"
                  height="240"
                  loading="lazy"
                  decoding="async"
                  className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <p className="px-3 py-2.5 text-center text-xs font-bold text-slate-700 group-hover:text-blue-600">
                  {category.name}
                </p>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function SearchPage() {
  const { q, products, total, categories } = Route.useLoaderData()

  useEffect(() => {
    if (q) trackSearch(q, total)
  }, [q, total])

  return (
    <div className="min-h-screen bg-slate-50">
      <TopBar />
      <HeaderMiddle />
      <Navbar />

      <div className="bg-slate-900 py-14">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex items-center gap-1.5 text-xs text-slate-400 mb-4">
            <a href="/" className="hover:text-white transition-colors">Beranda</a>
            <FontAwesomeIcon icon={faChevronRight} className="w-3 h-3" />
            <span className="text-white font-medium">Pencarian</span>
          </nav>
          <h1 className="text-4xl font-black text-white uppercase tracking-tight mb-2">Hasil Pencarian</h1>
          <p className="text-slate-300 text-sm max-w-2xl">
            {q ? `Menampilkan ${total} produk untuk kata kunci "${q}".` : 'Masukkan kata kunci produk pada kolom pencarian di atas.'}
          </p>
        </div>
      </div>

      <WhatsAppChannelSection />

      <main className="max-w-7xl mx-auto px-4 py-10">
        {!q ? (
          <div className="rounded-2xl border border-slate-200 bg-white px-6 py-16 text-center text-slate-500">
            Ketik nama produk di kolom pencarian lalu tekan tombol Cari.
          </div>
        ) : products.length === 0 ? (
          <EmptySearchState q={q} categories={categories} />
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
              {products.map((product) => (
                <SearchResultCard key={product.id} product={product} />
              ))}
            </div>
            {/* WA fallback on the RESULTS state too — searchers are the site's
                highest-intent visitors (GA4 Jun–Aug 2026: 46 users, 2 leads)
                but only the empty state offered a chat path. */}
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white px-6 py-6">
              <div className="text-center sm:text-left">
                <p className="text-base font-bold text-slate-900">Belum menemukan model yang dicari?</p>
                <p className="mt-1 text-sm text-slate-500">
                  Kirim nama atau foto barang kepada admin. Admin akan memeriksa katalog, stok, dan quotation untuk jumlah yang Anda perlukan.
                </p>
              </div>
              <a
                href={`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(`Halo Dropify, saya mencari "${q}". Mohon periksa stok dan siapkan quotation untuk jumlah ___.`)}`}
                {...whatsAppCtaProps('search', { cta_placement: 'search_results', search_term: q })}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex flex-shrink-0 items-center justify-center gap-2 rounded-lg bg-green-700 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-green-800"
              >
                <FontAwesomeIcon icon={faWhatsapp} className="h-4 w-4" />
                Periksa stok via WhatsApp
              </a>
            </div>
          </>
        )}
      </main>

      <Footer />
    </div>
  )
}
